import { useState, useRef, useEffect, useContext, useMemo } from 'react';

import LcEdaCanvas from '@lcsc-ui/components/LcEdaCanvas';
import SvgIcon from '@lcsc-ui/components/SvgIcon';
import { LayoutContext } from '@lcsc-ui/layouts/baseLayout/provider';
import { buriedPoint } from '@lcsc-ui/utils/buriedPoint';
import sendAxios from '@lcsc-ui/utils/request';
import Utils from '@lcsc-ui/utils/Utils';
import { message } from 'antd';
import Cookies from 'js-cookie';

import API from '@/apis';
import CompareDialog from '@/components/compareDialog';
import FavoriteModel from '@/components/favoriteModel';
import Image from '@/components/Image';
import { defaultImg, sameImgNameList } from '@/constants';
import useProductStore from '@/store/product';

export default function ProductImage({ data }) {
  const { resourceData } = useContext(LayoutContext);
  const { projectConfigVO } = resourceData;
  const productRecord = data?.productRecord || {};

  const { productAsyncData } = useProductStore();

  const [idx, setIdx] = useState(0);

  const imgList = useMemo(() => {
    return (
      productRecord?.luceneBreviaryImageUrls?.split('<$>')?.map((item) => ({
        type: 'img',
        url: item,
      })) || []
    );
  }, [productRecord?.luceneBreviaryImageUrls]);

  const hasImage = useMemo(() => {
    return !!imgList.filter((item) => item?.url).length;
  }, [imgList]);

  const [svgData, setSvgData] = useState([]);

  const [svgDataFetched, setSvgDataFetched] = useState(false);

  const [svgList, setSvgList] = useState([]);

  const previewList = useMemo(() => {
    return [...imgList, ...svgList];
  }, [imgList, svgList]);

  const visibleThumbnails = useMemo(() => {
    const maxThumbnails = 3;
    let start = 0;
    let end = Math.min(maxThumbnails, previewList.length);

    if (previewList.length > maxThumbnails) {
      start = Math.max(0, idx - Math.floor(maxThumbnails / 2));
      start = Math.min(start, previewList.length - maxThumbnails);
      end = start + maxThumbnails;
    }

    return previewList.slice(start, end);
  }, [previewList, idx]);

  const [isCollect, setIsCollect] = useState(false);
  const [sameImg, setSameImg] = useState({});
  const [isShowCompareDialog, setIsShowCompareDialog] = useState(false);
  const [compareList, setCompareList] = useState([]);
  const [isShowFavoriteModel, setIsShowFavoriteModel] = useState(false);
  const [bigImg, setBigImg] = useState({
    url: '',
    bigX: 0,
    bigY: 0,
    maskX: 0,
    maskY: 0,
  });
  const isOversea = data?.isOversea;

  // 更新收藏状态
  useEffect(() => {
    if (productAsyncData?.isFetched) {
      setIsCollect(productAsyncData?.isFavorite);
    }
  }, [productAsyncData]);

  useEffect(() => {
    if (data?.isOversea) {
      let obj = sameImgNameList.find((v) => {
        if (v.code === data?.provider) {
          return v;
        }
      });
      // 供应商G14102-香港-Texas Instruments图片使用TI logo
      if (data?.vendorCode === 'G14102') {
        obj = sameImgNameList.find((v) => {
          if (v.code === 'ti_ovs') {
            return v;
          }
        });
      }
      setSameImg(obj);
    } else {
      getSelectProductFavorite();
      getSvgData();
    }
  }, [data]);

  const imageRef = useRef(null);

  const maskWidth = 120; // 遮罩宽度
  const imgWidth = 260; // 图片宽度
  const bigImgWidth = 900; // 大图宽度

  // 获取收藏状态
  function getSelectProductFavorite() {
    if (Utils.isLogined()) {
      sendAxios({
        method: 'get',
        baseURL: 'lcscMemberUrl',
        url: `${API.selectProductFavorite}?productIds=${data?.productRecord?.productId}`,
      }).then((res) => {
        if (res?.result?.length) {
          setIsCollect(true);
        } else {
          setIsCollect(false);
        }
      });
    }
  }

  function handleMouseMoveEvent(e) {
    let image = imgList[idx]?.url;
    if (!image) {
      return;
    }
    image = image.replace('product/breviary', 'product/source');
    const clientLeft = imageRef.current?.getBoundingClientRect()?.left || 0;
    const clientTop = imageRef.current?.getBoundingClientRect()?.top || 0;
    const moveX = e.clientX - clientLeft;
    const moveY = e.clientY - clientTop;
    const scale = bigImgWidth / imgWidth;
    let maskX = moveX - maskWidth / 2;
    let maskY = moveY - maskWidth / 2;
    if (maskX <= 0) {
      maskX = 0;
    } else if (maskX >= imgWidth - maskWidth) {
      maskX = imgWidth - maskWidth;
    }
    if (maskY <= 0) {
      maskY = 0;
    } else if (maskY >= imgWidth - maskWidth) {
      maskY = imgWidth - maskWidth;
    }
    setBigImg({
      ...bigImg,
      url: image,
      bigX: -maskX * scale,
      bigY: -maskY * scale,
      maskX,
      maskY,
    });
  }

  function handleMouseLeaveEvent() {
    setBigImg({
      ...bigImg,
      url: '',
    });
  }

  // 加载EDA图
  const getSvgData = () => {
    sendAxios({
      method: 'GET',
      baseURL: 'https://lceda.cn/api/products/' + productRecord?.productCode + '/svgs',
    })
      .then((res) => {
        if (res.success) {
          const data = res.result || [];
          if (data.length) {
            setSvgData(data);
            setSvgList([
              { type: 'svg', name: 'sch' },
              { type: 'svg', name: 'pcb' },
            ]);
          }
        }
      })
      .finally(() => {
        setSvgDataFetched(true);
      });
  };

  const preImg = () => {
    if (idx !== 0) {
      setIdx(idx - 1);
    }
  };

  const nextImg = () => {
    if (idx < previewList.length - 1) {
      setIdx(idx + 1);
    }
  };

  const handleOk = () => {
    setIsShowFavoriteModel(false);
  };

  const HotDiscountIcon = () => {
    let priceLabel = '';

    if (productRecord?.discountBuyLimitProduct?.discountMember === 'plus') {
      if (productRecord?.discountBuyLimitProduct?.discountType === 'rate') {
        priceLabel = 'PLUS折扣';
      } else if (productRecord?.discountBuyLimitProduct?.discountType === 'reduce') {
        priceLabel = 'PLUS特价';
      }
    } else {
      if (productRecord?.discountBuyLimitProduct?.discountType === 'reduce') {
        priceLabel = '限量特价';
      } else if (productRecord?.discountBuyLimitProduct?.discountPrice) {
        const discountPrice = Utils.priceRound(productRecord?.discountBuyLimitProduct?.discountPrice * 10, 2);
        priceLabel = `${discountPrice}折`;
      }
    }

    const className =
      'rounded-tl-[8px] min-w-[70px] absolute bg-gradient-to-r px-[10px] from-[#FF4960] to-[#FFD480] z-[50] top-[0px] h-[30px] rounded-br-[50px] text-[#fff] text-[14px] leading-[30px]';

    return (
      <>
        {productAsyncData?.productRecord?.isDisplay === false ? (
          <>
            <div className="rounded-tl-[8px] min-w-[70px] absolute bg-[#0000005b] px-[10px] z-[50] top-[0px] h-[30px] rounded-br-[50px] text-[#fff] text-[14px] leading-[30px]">
              已下架
            </div>
          </>
        ) : productRecord?.discountBuyLimitProduct && productRecord?.discountBuyLimitProduct?.showTag && priceLabel ? (
          <>
            <div className={className}>
              <span>
                {data?.isHot ? '爆款 x ' : ''}
                {priceLabel}
              </span>
            </div>
          </>
        ) : productAsyncData?.productRecord?.productPriceDiscountList &&
          productAsyncData?.productRecord?.productPriceDiscountList[0] &&
          !productRecord?.discountBuyLimitProduct &&
          data?.discountPriceMaxDiscount ? (
          <div className={className}>
            <span>
              {data?.isHot ? '爆款 x ' : ''}
              {`${Utils.priceRound(data?.discountPriceMaxDiscount * 10, 2)}折`}
            </span>
          </div>
        ) : data?.isProductRebate && !data?.isGuidePrice && data?.rebate > 0 ? (
          <div className={className}>
            <span>
              {data?.isHot ? '爆款 x ' : ''}
              返采购晶
            </span>
          </div>
        ) : !data?.productRecord?.selfProductCode && data?.productRecord?.saleRuleGroupId == 10054 ? ( // p转c商品不展示预售标签
          <div className={className}>
            <span>
              {data?.isHot ? '爆款 x ' : ''}
              预售商品
            </span>
          </div>
        ) : (
          data?.isHot && (
            <div className={className}>
              <span>爆款</span>
            </div>
          )
        )}
      </>
    );
  };

  const thumbnailList = useMemo(() => {
    return (
      <div className="w-[260px] flex items-center h-[48px] relative mt-[10px]">
        <button
          aria-label="切换上一张图片"
          className="w-[14px] h-[12px] border-solid border-[#B8BEC9] border-r-[2px] border-b-[2px] transform rotate-[135deg] hover:border-[#199FE9] cursor-pointer ml-[6px] mr-[6px]"
          onClick={preImg}
        ></button>
        <ul className="flex-1 flex items-center justify-center">
          {visibleThumbnails?.map((item) => {
            const actualIndex = previewList.findIndex((i) => i === item);
            return (
              <li key={actualIndex} className="mr-[20px] last-of-type:mr-0">
                {item.type === 'img' && (
                  <Image
                    className={`cursor-pointer rounded-[4px] w-[48px] h-[48px] ${actualIndex === idx ? 'border-solid border-[1px] border-[#199FE9]' : ''}`}
                    src={item.url}
                    alt={`${productRecord?.productModel}商品缩略图`}
                    height={48}
                    width={48}
                    fallback={defaultImg}
                    onClick={() => setIdx(actualIndex)}
                  />
                )}
                {item.type === 'svg' && (
                  <div
                    className={`flex items-center justify-center text-[12px] font-bold cursor-pointer rounded-[4px] w-[48px] h-[48px] border-solid border-[1px] border-[#e5e7eb] ${actualIndex === idx ? '!border-[#199FE9]' : ''}`}
                    onClick={() => setIdx(actualIndex)}
                  >
                    {item.name === 'sch' ? '引脚图' : '焊盘图'}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <button
          aria-label="切换下一张图片"
          className="w-[14px] h-[12px] border-solid border-[#B8BEC9] border-r-[2px] border-b-[2px] transform rotate-[-45deg] hover:border-[#199FE9] cursor-pointer ml-[6px] mr-[6px]"
          onClick={nextImg}
        ></button>
      </div>
    );
  }, [productRecord?.productModel, imgList, idx]);

  const CollectAndCompare = () => {
    const [isHover, setIsHover] = useState(false);
    const [isHoverContrast, setIsHoverContras] = useState(false);

    const hoverFn = (isHover) => {
      setIsHover(isHover);
    };

    // 跳登录和注册
    const gotoLogin = (type) => {
      const url = Utils.getCasTypeUrl(type, location.href);
      window.open(url, '_self');
    };

    const collectFn = () => {
      buriedPoint({
        ty: 'click',
        cp: 'product_detail',
        op: 'add_collect',
        data: { pc: data?.productRecord?.productId },
      });
      sendAxios({
        method: 'get',
        baseURL: 'lcscMemberUrl',
        url: `${API.asyncFavoriteAdd}?productId=${data?.productRecord?.productId}`,
        formData: true,
      })
        .then((result) => {
          if (result.code === 200) {
            if (isCollect) {
              message.success('取消收藏成功！');
              setIsCollect(false);
              setIsHover(false);
            } else {
              setIsCollect(true);
              setIsShowFavoriteModel(true);
            }
          } else if (result.needLogin) {
            gotoLogin();
          } else if (result.msg) {
            message.error(result.msg);
          }
        })
        .catch((error) => {
          message.error(error.msg);
        });
    };

    const hoverContrasFn = () => {
      setIsHoverContras(!isHoverContrast);
    };

    // 从cookie中获取对比商品信息
    const selectProductFromCookie = () => {
      const product_compare_bid = Cookies.get('product_compare_bid');
      const product_compare = Cookies.get('product_compare');
      if (!product_compare && product_compare_bid) {
        Cookies.set('product_compare_bid', '', {
          domain: '.szlcsc.com',
          path: '/',
        });
      }
      if (product_compare && !product_compare_bid) {
        Cookies.set('product_compare_bid', Utils.getUuid(), {
          domain: '.szlcsc.com',
          path: '/',
        });
      }
      sendAxios({
        method: 'GET',
        baseURL: 'lcscSearchUrl',
        url: API.compareProductGetV2,
      }).then(({ result }) => {
        setCompareList(result);
        setIsShowCompareDialog(true);
      });
    };

    // 点击对比
    const clickCompare = () => {
      buriedPoint({
        ty: 'click',
        cp: 'product_detail',
        op: 'add_compare',
        data: { pc: data?.productRecord?.productId, bi: Cookies.get('product_compare_bid') },
      });
      sendAxios({
        method: 'get',
        baseURL: 'lcscItemUrl',
        url: `${API.compareProductAddV2}?productId=${data?.productRecord?.productId}`,
      }).then(({ result, code, msg }) => {
        if (code === 200) {
          switch (result?.check_result) {
            case 'existed':
              message.error('对比栏商品已存在');
              break;
            case 'fail':
              message.error('对比栏已满！');
              break;
            case 'success':
              break;
            default:
          }
          selectProductFromCookie();
        } else {
          message.error(msg);
        }
      });
    };

    return (
      <div className="flex items-center mt-[17px] text-[#54657D] text-[12px]">
        <button
          id="item-collect"
          className={`flex items-center cursor-pointer ${isHover || isCollect ? 'text-[#FAAD14]' : ''}`}
          onMouseEnter={() => hoverFn(true)}
          onMouseLeave={() => hoverFn(false)}
          onClick={collectFn}
        >
          <SvgIcon
            app="item"
            iconName={isHover || isCollect ? 'collect-hover' : 'collect'}
            className="w-[20px] h-[20px]"
          />
          <span className="ml-[4px]">{isCollect ? '已收藏' : '收藏'}</span>
        </button>
        <div className="w-[1px] h-[18px] bg-[#B8BEC9] m-[0_10px]"></div>
        <button
          id="item-compare"
          className="flex items-center cursor-pointer hover:text-[#FAAD14]"
          onMouseEnter={hoverContrasFn}
          onMouseLeave={hoverContrasFn}
          onClick={clickCompare}
        >
          <SvgIcon app="item" iconName="contrast" className="w-[20px] h-[20px]" />
          <span className="ml-[4px]">对比</span>
        </button>
      </div>
    );
  };

  const localityImg = (
    <div className="relative">
      {(!svgList.length || previewList[idx]?.type === 'img') && (
        <a
          data-spm="img"
          aria-label={`查看${productRecord?.productModel}的PCB引脚图、焊盘图、高清图`}
          href={`${projectConfigVO?.lcscItemUrl}/product/jpg_${data?.productRecord?.productId}.html`}
          target="_blank"
          rel={!hasImage && 'nofollow'}
        >
          <div
            className="relative w-[260px] h-[260px]"
            onMouseMove={(e) => {
              handleMouseMoveEvent(e);
            }}
            onMouseLeave={(e) => {
              handleMouseLeaveEvent(e);
            }}
          >
            <HotDiscountIcon />
            <Image
              className={`mb-[13px] rounded-[8px] h-[${imgWidth}px]`}
              src={previewList[idx]?.url?.replace('product/breviary', 'product/source') || defaultImg}
              title="点击查看大图"
              alt={`${productRecord?.productModel}实物图`}
              height={imgWidth}
              width={imgWidth}
              ref={imageRef}
              priority={true} // 预加载图片
              fallback={defaultImg}
            />
            {bigImg.url !== '' ? (
              <div
                className={`absolute bg-[lightblue] opacity-[0.4] z-[99]`}
                style={{
                  width: maskWidth + 'px',
                  height: maskWidth + 'px',
                  top: bigImg.maskY + 'px',
                  left: bigImg.maskX + 'px',
                }}
              ></div>
            ) : (
              <></>
            )}
          </div>
        </a>
      )}
      {/** 引脚焊盘图 */}
      <LcEdaCanvas
        containerId="containerId2"
        className={previewList[idx]?.name !== 'sch' ? 'hidden' : ''}
        type="sch"
        size={imgWidth}
        data={svgData}
        svgDataFetched={svgDataFetched}
        productModel={productRecord?.productModel}
      />
      <LcEdaCanvas
        containerId="containerId2"
        className={previewList[idx]?.name !== 'pcb' ? 'hidden' : ''}
        type="pcb"
        size={imgWidth}
        data={svgData}
        svgDataFetched={svgDataFetched}
        productModel={productRecord?.productModel}
      />
      {/** 查看大图 */}
      {bigImg.url !== '' ? (
        <section
          className={`size-[400px] absolute left-[280px] top-[0px] z-[4] overflow-hidden border-solid border-[1px] border-[#e8e8e8]`}
          style={{
            backgroundImage: `url(${bigImg.url})`,
            backgroundSize: `${bigImgWidth}px ${bigImgWidth}px`,
            backgroundPositionX: bigImg.bigX + 'px',
            backgroundPositionY: bigImg.bigY + 'px',
            backgroundRepeat: 'no-repeat',
          }}
        ></section>
      ) : (
        <></>
      )}
      {visibleThumbnails.length ? thumbnailList : <></>}
      <p className="text-[#54657D] text-[12px] mt-[4px]">温馨提醒：图片仅供参考，商品以实物为准</p>
      {/** 下架商品不展示收藏和对比按钮 */}
      {productAsyncData?.productRecord?.isDisplay !== false && <CollectAndCompare />}
    </div>
  );

  const overseaImg = (
    <ul className="relative">
      <li>
        {sameImg?.name && sameImg?.format && (
          <Image
            className="size-[260px] mb-[13px] rounded-[8px]"
            src={
              sameImg?.src ||
              `https://static.szlcsc.com/ecp/assets/web/common/images/productItem/${sameImg?.name}.${sameImg?.format}`
            }
            alt={`${productRecord?.productModel}实物图`}
            height={260}
            width={260}
            fallback={defaultImg}
          />
        )}
      </li>
      <p className="text-[#54657D] text-[12px] mt-[4px]">温馨提醒：图片仅供参考，商品以实物为准</p>
    </ul>
  );

  return (
    <>
      {isOversea ? overseaImg : localityImg}
      <CompareDialog
        row={compareList}
        isShowCompareDialog={isShowCompareDialog}
        entryType="item_details"
        setIsShowCompareDialog={(val) => setIsShowCompareDialog(val)}
      />
      <FavoriteModel
        isShowFavoriteModel={isShowFavoriteModel}
        setIsShowFavoriteModel={(val) => setIsShowFavoriteModel(val)}
        handleOk={handleOk}
      />
    </>
  );
}
